import { type VariantProps, cva } from "class-variance-authority";
import { BriefcaseBusiness, FileText } from "lucide-react";
import * as React from "react";
import { Separator } from "../atoms/separator.js";
import { Skeleton } from "../atoms/skeleton.js";
import { cx } from "../internal/cx.js";
import { BillingContactCard } from "../molecules/billing-contact-card.js";
import {
  type BillingOverviewPaymentMethod,
  BillingOverviewPaymentMethodCard,
} from "../molecules/billing-overview-payment-method-card.js";
import { NoticeCard } from "../molecules/notice-card.js";
import { SummaryCard } from "../molecules/summary-card.js";
import { BillingOverviewRecentPaymentCard } from "./billing-overview-recent-payment-card.js";
import { SectionHeader } from "./section-header.js";

const billingOverviewSectionVariants = cva("cw-v2-billing-overview-section", {
  variants: {
    billingType: {
      standard: "cw-v2-billing-overview-section--standard",
      // Replaces the former "websurance" / "child-company" variants. This is a
      // styling hook only — all copy now arrives via `billingNotice`, so no
      // vendor name and no billing rule lives in the design system.
      delegated: "cw-v2-billing-overview-section--delegated",
    },
    layout: {
      default: "cw-v2-billing-overview-section--layout-default",
      paired: "cw-v2-billing-overview-section--layout-paired",
    },
  },
  defaultVariants: {
    billingType: "standard",
    layout: "default",
  },
});

export interface BillingContact {
  /** Billing contact name. */
  name: React.ReactNode;
  /** Contact role/title. */
  role?: React.ReactNode;
  /** Contact phone number or link node. */
  phone?: React.ReactNode;
  /** Contact email or link node. */
  email?: React.ReactNode;
  /** Contact address. */
  address?: React.ReactNode;
}

export interface BillingStatementSummary {
  /** Summary card title. */
  title?: React.ReactNode;
  /** Statement coverage period. */
  period?: React.ReactNode;
  /** Already-formatted total amount. */
  totalAmount?: React.ReactNode;
  /** Enrolled member/employee count. */
  enrolledCount?: React.ReactNode;
  /** Optional statement action. */
  action?: React.ReactNode;
}

export interface RecentPaymentSummary {
  /** Recent payment card title. */
  title?: React.ReactNode;
  /** Coverage period covered by this payment. */
  coveragePeriod?: React.ReactNode;
  /** Already-formatted payment amount. */
  amount?: React.ReactNode;
  /** Payment status label. */
  status?: React.ReactNode;
  /** Draft date or payment date copy. */
  draftDate?: React.ReactNode;
  /** Already-formatted payment method line. */
  paymentMethod?: React.ReactNode;
  /** Covered employee count/value. */
  coveredEmployees?: React.ReactNode;
  /** Optional note below the card. */
  note?: React.ReactNode;
  /** Custom benefits statement action. */
  action?: React.ReactNode;
  /** Fires when the benefits statement button is clicked. */
  onViewStatement?: () => void;
  /** Benefits statement button label. */
  viewStatementLabel?: React.ReactNode;
}

/**
 * Copy explaining that someone other than Cakewalk handles this company's
 * billing. Supplied fully rendered by the caller — the API substitutes any
 * tokens server-side — so this component holds no billing rules and names no
 * vendor. Presence of this prop is what switches the section into notice mode.
 */
export interface BillingNotice {
  /** Notice headline, e.g. "Billing is managed through Websurance". */
  title: React.ReactNode;
  /** The explanation shown below the headline. */
  description?: React.ReactNode;
  /** Link-out label. Rendered only alongside `ctaUrl`. */
  ctaLabel?: React.ReactNode;
  /** Link-out target. Opened in a new tab with noopener/noreferrer. */
  ctaUrl?: string;
}

export interface BillingOverviewSectionProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title">,
    VariantProps<typeof billingOverviewSectionVariants> {
  /** Section heading. Default "Billing Overview". */
  title?: React.ReactNode;
  /** Supporting copy shown below the section heading. */
  description?: React.ReactNode;
  /** Billing contact details. */
  billingContact?: BillingContact | null;
  /** Payment methods; the first default/verified method is preferred. */
  paymentMethods?: BillingOverviewPaymentMethod[];
  /** Styling hook only. Copy is driven entirely by `billingNotice`. */
  billingType?: "standard" | "delegated";
  /**
   * When set, the contact + payment-method cards are replaced by this notice —
   * Cakewalk does not handle billing for this company, so its own payment
   * details are not the right thing to show.
   */
  billingNotice?: BillingNotice | null;
  /** Optional current statement summary. */
  statement?: BillingStatementSummary | null;
  /** Optional recent payment summary. */
  recentPayment?: RecentPaymentSummary | null;
  /** Shows section-level skeleton placeholders while billing data loads. */
  isLoading?: boolean;
  /** Fires when the empty payment method CTA is clicked. */
  onAddPaymentMethod?: () => void;
  /** Fires from manage billing/history actions. */
  onViewBillingHistory?: () => void;
}

/**
 * Owner cockpit billing overview section. It composes contact, payment method,
 * statement, and recent payment cards using already-fetched display data.
 */
export const BillingOverviewSection = React.forwardRef<HTMLElement, BillingOverviewSectionProps>(
  function BillingOverviewSection(
    {
      billingContact,
      billingNotice,
      billingType,
      className,
      description = "Review billing contacts, payment methods, and recent payment activity.",
      isLoading = false,
      layout,
      onAddPaymentMethod,
      onViewBillingHistory,
      paymentMethods = [],
      recentPayment,
      statement,
      title = "Billing Overview",
      ...props
    },
    ref,
  ) {
    // The notice itself decides the mode. `billingType` only carries styling, so
    // a caller cannot set the variant and silently get an empty notice card.
    const isStandard = !billingNotice;
    const resolvedBillingType = billingType ?? (billingNotice ? "delegated" : "standard");
    const paymentMethod =
      paymentMethods.find((method) =>
        method.badges?.some((badge) => badge.type === "default" || badge.type === "verified"),
      ) ??
      paymentMethods[0] ??
      null;

    return (
      <section
        ref={ref}
        className={cx(
          billingOverviewSectionVariants({ billingType: resolvedBillingType, layout }),
          className,
        )}
        {...props}
      >
        <SectionHeader
          description={description}
          icon={<BriefcaseBusiness aria-hidden size={18} />}
          title={title}
        />

        {isLoading ? (
          <div className="cw-v2-billing-overview-section__content-grid" aria-hidden="true">
            <Skeleton className="min-h-40 rounded-xl" />
            <Skeleton className="min-h-40 rounded-xl" />
          </div>
        ) : billingNotice ? (
          <NoticeCard
            action={
              billingNotice.ctaUrl && billingNotice.ctaLabel ? (
                <a
                  className="cw-v2-billing-overview-section__notice-action"
                  href={billingNotice.ctaUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {billingNotice.ctaLabel}
                </a>
              ) : undefined
            }
            description={billingNotice.description}
            title={billingNotice.title}
            tone="info"
          />
        ) : (
          <div className="cw-v2-billing-overview-section__content-grid">
            {billingContact ? (
              <BillingContactCard
                address={billingContact.address}
                bordered={false}
                contactName={billingContact.name}
                contactRole={billingContact.role}
                email={billingContact.email}
                phone={billingContact.phone}
              />
            ) : null}
            <BillingOverviewPaymentMethodCard
              addActionLabel="Set up payment method"
              onAddPaymentMethod={onAddPaymentMethod}
              onManageBilling={onViewBillingHistory}
              paymentMethod={paymentMethod}
            />
          </div>
        )}

        {statement ? (
          <>
            <Separator />
            <SummaryCard
              action={statement.action}
              bordered={false}
              rows={[
                { id: "period", label: "Period", value: statement.period },
                { id: "enrolled", label: "Enrolled", value: statement.enrolledCount },
                { id: "total-monthly", label: "Total monthly", value: statement.totalAmount },
              ]}
              title={
                <span className="cw-v2-billing-overview-section__summary-title">
                  <FileText aria-hidden size={18} />
                  <span>{statement.title ?? "Compact benefits statement"}</span>
                </span>
              }
            />
          </>
        ) : null}

        {recentPayment ? (
          <BillingOverviewRecentPaymentCard
            amount={recentPayment.amount}
            coveragePeriod={recentPayment.coveragePeriod}
            coveredEmployees={recentPayment.coveredEmployees}
            draftDate={recentPayment.draftDate}
            note={recentPayment.note}
            onViewHistory={onViewBillingHistory}
            onViewStatement={recentPayment.onViewStatement}
            paymentMethod={recentPayment.paymentMethod}
            statementAction={recentPayment.action}
            status={recentPayment.status}
            title={recentPayment.title}
            viewStatementLabel={recentPayment.viewStatementLabel}
          />
        ) : null}
      </section>
    );
  },
);
BillingOverviewSection.displayName = "BillingOverviewSection";

export const V2BillingOverviewSection = BillingOverviewSection;
export type V2BillingOverviewSectionProps = BillingOverviewSectionProps;
export type V2BillingContact = BillingContact;
export type V2BillingNotice = BillingNotice;
export type V2BillingStatementSummary = BillingStatementSummary;
export type V2RecentPaymentSummary = RecentPaymentSummary;
